import { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import type { StudioId } from '@/domain/types';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from '@/api/queryClient';
import { useCalendarView, type CalendarViewBundle } from '@/api/views';
import { useBrainStore } from '@/state/brainStore';
import { useSimulationStore } from '@/state/simulationStore';
import { useSessionStore } from '@/state/sessionStore';
import { aud, customerName, simTimeFull } from '@/utils/format';
import { bookingPriceAud } from '@/domain/pricing';
import { ChevronLeft, ChevronRight, Calendar, Plus, History } from 'lucide-react';
import { format, addDays, startOfWeek, startOfDay, isSameDay, differenceInCalendarDays } from 'date-fns';
import { startNoEarlierThanNow } from '@/utils/slotClip';
import clsx from 'clsx';
import NewBookingModal from '@/components/NewBookingModal';
import BookingDetailCard from '@/components/BookingDetailCard';
import CalendarFilterBar from '@/components/CalendarFilterBar';
import MiniCalendarPopover from '@/components/MiniCalendarPopover';
import {
  EMPTY_CALENDAR_FILTERS,
  bookingMatchesFilters,
  type CalendarFilters,
} from '@/utils/calendarFilters';
import { bookingTileTone, bookingStatusTone, depositPaidLevel, dayOffTileTone, waiverTone } from '@/utils/statusTone';
import { bookingTileState, GHOST_TILE_CLASS, tileDeposit } from '@/utils/bookingTileState';
import { isLiveBooking } from '@atink/domain';
import { pickLatestWaiver } from '@/domain/waiverFlow';
import { RESCHEDULE_REASON_LABEL } from '@/utils/bookingSummary';
import { placeBookingFromDeposit } from '@/domain/depositFlow';
import { payAtStudioDepositFor } from '@/domain/depositStatus';
import { rescheduleBooking } from '@/domain/rescheduleFlow';
import RescheduleReasonChooser from '@/components/calendar/RescheduleReasonChooser';
import { useUIStore } from '@/state/uiStore';
import { Sparkles } from 'lucide-react';
import DayOffModal from '@/components/DayOffModal';
import SlotActionChooser from '@/components/SlotActionChooser';
import DoubleDayBadge from '@/components/DoubleDayBadge';
import StackedHoldsTile from '@/components/StackedHoldsTile';
import { groupOverbookableHolds, type HoldCluster } from '@/utils/stackedHolds';
import { resolveDayOff } from '@/domain/artistAdmin';
import { countDoubleDaysSinceLastDayOff, sessionsThisWeek, WEEKLY_SESSION_BUDGET } from '@/domain/workload';
import { useMakeSaleDraftStore } from '@/state/makeSaleDraftStore';
import ReconnectingNotice from '@/components/shell/ReconnectingNotice';
import { actorLabel, currentActorId } from '@/domain/actor';
import { scopeOnRoute, ALL_STUDIOS_SCOPE } from '@/domain/studioScope';
import NetworkCalendar from '@/screens/NetworkCalendar';

const SLOT_HEIGHT_PX = 30;
const SLOT_MIN = 30;
const FIRST_HOUR = 9;
const LAST_HOUR = 21;

// Calendar cards have coloured fills. Opaque pill surfaces keep their labels
// legible independently of the deposit shade underneath them.
const CALENDAR_PILL = 'rounded-full border px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide leading-tight';
const CALENDAR_PILL_TONE = {
  cream: 'border-stone-300 bg-stone-100 text-stone-800',
  gold: 'border-amber-300 bg-amber-100 text-amber-950',
  blood: 'border-red-300 bg-red-100 text-red-900',
  mint: 'border-emerald-300 bg-emerald-100 text-emerald-900',
};

function buildHourRows(): string[] {
  const rows: string[] = [];
  for (let h = FIRST_HOUR; h < LAST_HOUR; h += 1) {
    rows.push(`${h.toString().padStart(2, '0')}:00`);
    rows.push(`${h.toString().padStart(2, '0')}:30`);
  }
  return rows;
}

type ArtistLike = ReturnType<typeof useBrainStore.getState>['artists'][string];

/** Full-day placeholder info for an artist on a given day, or null when working. */
function dayOffFor(a: ArtistLike, day: Date) {
  return resolveDayOff(a.availability, day);
}

function sessionLabel(len: string): string {
  return len === 'half-day'
    ? 'Half day'
    : len === 'full-day'
      ? 'Full day'
      : len === 'multi-session'
        ? 'Multi'
        : 'Small';
}

function StudioCalendarInner() {
  const { studioId } = useParams<{ studioId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  // Keep the top-bar switcher in step with a concrete studio this route shows (a
  // deep link or rail hop), but never replace "All studios": the page reads its
  // studio from the URL and the rest of the app stays network-wide (75n8).
  const setActiveStudio = useSessionStore((s) => s.setActiveStudio);
  useEffect(() => {
    const active = useSessionStore.getState().activeStudio;
    const next = scopeOnRoute(active, studioId);
    if (next !== active) setActiveStudio(next as StudioId);
  }, [studioId, setActiveStudio]);
  const focusState = location.state as
    | {
        focusDayIso?: string;
        focusArtistId?: string;
        bookForCustomerId?: string;
        placeBookingDepositId?: string;
        moveBookingId?: string;
        rebookForBookingId?: string;
        rebookArtistId?: string;
        rebookMinDateIso?: string;
      }
    | null;
  const bookForCustomerId = focusState?.bookForCustomerId;
  const placeDepositId = focusState?.placeBookingDepositId;
  const rebookForBookingId = focusState?.rebookForBookingId;
  const rebookMinDateIso = focusState?.rebookMinDateIso;
  // Raw (unwindowed) brainStore lookups, kept only for a few deep-link /
  // cross-reference flows that resolve one record by id regardless of the
  // visible calendar week — a deposit-in-hand, or a rebook off an old,
  // already-completed booking that will never fall inside the live view's
  // forward-looking window. Everything else below reads the live, per-week
  // `calendarData` (useCalendarView) instead of the full store.
  const rawDeposits = useBrainStore((s) => s.deposits);
  const rawBookings = useBrainStore((s) => s.bookings);
  const rawCustomers = useBrainStore((s) => s.customers);
  const placeDeposit = placeDepositId ? rawDeposits[placeDepositId] : undefined;
  const pushNotification = useUIStore((s) => s.pushNotification);
  const studios = useBrainStore((s) => s.studios);
  const artists = useBrainStore((s) => s.artists);
  const clockIso = useSimulationStore((s) => s.clockIso);

  // When placing a paid deposit that carries a discussed slot, land the agent on
  // that artist/day. Explicit focus state (from a deep link) still wins.
  const groundedSlot = placeDeposit?.discussedSlot;
  // A rebook pick lands on the earliest healed day (2 weeks out) by default.
  const initialDay = focusState?.focusDayIso
    ? new Date(focusState.focusDayIso)
    : rebookMinDateIso
      ? new Date(rebookMinDateIso)
      : groundedSlot
        ? new Date(groundedSlot.startIso)
        : new Date(clockIso);
  const [view, setView] = useState<'day' | 'week'>('day');
  const [pickedDay, setPickedDay] = useState(() => initialDay);
  const [weekStart, setWeekStart] = useState(() => startOfWeek(initialDay, { weekStartsOn: 1 }));

  // All calendar navigation routes through these two helpers so the invariant
  // `weekStart === startOfWeek(pickedDay)` always holds. The live fetch window
  // is derived from `weekStart` alone (see useCalendarView below), so moving the
  // picked day MUST move the week too — otherwise a day navigated to outside the
  // clock's week is never fetched, and its bookings never render (tv-k9b.18: the
  // sim clock only seeds the initial date, it must not pin what you can view).
  const goToDay = (d: Date) => {
    setPickedDay(d);
    setWeekStart(startOfWeek(d, { weekStartsOn: 1 }));
  };
  const goToWeek = (w: Date) => {
    const ws = startOfWeek(w, { weekStartsOn: 1 });
    setWeekStart(ws);
    setPickedDay(ws);
  };

  // Live per-view read: the calendar's data source is the per-studio,
  // per-week server view (falls back to a store-derived snapshot instantly
  // via placeholderData, then upgrades to the live fetch — see api/views.ts).
  // The visible window is always `[weekStart, weekStart+7)` — day view keeps
  // `pickedDay` synced into the current week (see the focusDayIso effect
  // below), so this one window covers both the day and week views.
  const fromIso = weekStart.toISOString();
  const toIso = addDays(weekStart, 7).toISOString();
  const { data: calendarData, isError } = useCalendarView(studioId, fromIso, toIso);
  const bookings = calendarData?.bookings ?? {};
  const customers = calendarData?.customers ?? {};
  const deposits = calendarData?.deposits ?? {};
  const waivers = calendarData?.waivers ?? {};
  const overrideRequests = calendarData?.overrideRequests ?? {};

  // Booking ids with a pending no-deposit override — tiles badge as "awaiting
  // approval" so a held-for-approval slot reads differently from an ordinary hold.
  const awaitingApprovalBookingIds = useMemo(
    () =>
      new Set(
        Object.values(overrideRequests)
          .filter((r) => r.status === 'pending')
          .map((r) => r.bookingId),
      ),
    [overrideRequests],
  );

  // Week view focuses a single artist; empty/stale ids fall back to the first
  // rostered artist via `activeWeekArtist` below.
  const [weekArtistId, setWeekArtistId] = useState('');
  const [newBooking, setNewBooking] = useState<{ artistId: string; startIso: string } | null>(null);
  const [slotChoice, setSlotChoice] = useState<{ artistId: string; startIso: string } | null>(null);
  const [dayOff, setDayOff] = useState<{ artistId: string; date: string } | null>(null);
  const [filters, setFilters] = useState<CalendarFilters>(EMPTY_CALENDAR_FILTERS);
  // Cancelled / no-show bookings are faint ghosts behind this toggle (W4 G8):
  // hidden by default, never blocking the slot they freed.
  const [showCancelled, setShowCancelled] = useState(false);
  const [detailBookingId, setDetailBookingId] = useState<string | null>(null);
  // A booking sent here from the Edit modal's "Reschedule on calendar" — the tile
  // is highlighted and a hint invites a drag to a new slot.
  const [movingId, setMovingId] = useState<string | null>(focusState?.moveBookingId ?? null);
  // A drag captured on the calendar, awaiting a mandatory reschedule reason
  // before it commits (15 Jul ruling: no reschedule without a reason).
  const [pendingMove, setPendingMove] = useState<{
    bookingId: string;
    artistId: string;
    startIso: string;
  } | null>(null);
  const focusArtistId =
    focusState?.focusArtistId ?? focusState?.rebookArtistId ?? groundedSlot?.artistId;

  // Rebook-pick mode: a slot chosen here is written back to the make-sale draft
  // and returns to the floor. A slot inside the 2-week healing window is held in
  // `pendingRebookSlot` until the manager confirms.
  const rebookBooking = rebookForBookingId ? rawBookings[rebookForBookingId] : undefined;
  const rebookCustomer = rebookBooking ? rawCustomers[rebookBooking.customerId] : undefined;
  const [pendingRebookSlot, setPendingRebookSlot] = useState<{
    artistId: string;
    startIso: string;
  } | null>(null);

  const confirmRebookSlot = (artistId: string, startIso: string) => {
    if (!rebookForBookingId) return;
    useMakeSaleDraftStore.getState().setPickedSlot(rebookForBookingId, startIso, artistId);
    navigate(`/floor/${rebookForBookingId}`);
  };

  useEffect(() => {
    if (focusState?.moveBookingId) setMovingId(focusState.moveBookingId);
  }, [focusState?.moveBookingId]);

  useEffect(() => {
    if (focusState?.focusDayIso) {
      const d = new Date(focusState.focusDayIso);
      setPickedDay(d);
      setWeekStart(startOfWeek(d, { weekStartsOn: 1 }));
      setView('day');
    }
  }, [focusState?.focusDayIso]);

  useEffect(() => {
    if (!focusArtistId) return;
    requestAnimationFrame(() => {
      document
        .querySelector(`[data-testid="day-col-${focusArtistId}"]`)
        ?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    });
  }, [focusArtistId, pickedDay]);

  const studio = studioId ? studios[studioId] : undefined;
  // tv-hnjy.6 (Anas 8 Oct): apprentices are bookable, so they get a column too;
  // the roster, leave and archive rules apply to them as to any artist. They
  // sit after the artists (order otherwise unchanged), so the week view's
  // default artist is never an apprentice.
  const studioArtists = useMemo(
    () =>
      Object.values(artists)
        .filter((a) => a.studioId === studioId && !a.archived)
        .sort((a, b) => Number(a.isApprentice) - Number(b.isApprentice)),
    [artists, studioId],
  );

  const activeWeekArtist =
    studioArtists.find((a) => a.id === weekArtistId) ?? studioArtists[0];

  // "Double days since the last day off" walks back past the visible week (75n7):
  // a double last Saturday must count on Monday. The live view only holds this
  // week, so count over every booking on hand, with the live week winning.
  const weekArtistDoubleDays = useMemo(
    () =>
      activeWeekArtist
        ? countDoubleDaysSinceLastDayOff(activeWeekArtist, Object.values({ ...rawBookings, ...bookings }), clockIso)
        : 0,
    [activeWeekArtist, rawBookings, bookings, clockIso],
  );

  const days = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)),
    [weekStart],
  );

  // Full-day session units this week for the artist in the week view (a double
  // day counts as 2). Display/warning only - shown beside the double-day badge.
  const weekSessionUnits = activeWeekArtist
    ? sessionsThisWeek(activeWeekArtist, Object.values(bookings), days.map((d) => format(d, 'yyyy-MM-dd')))
    : 0;

  const rows = buildHourRows();

  const studioBookings = useMemo(
    () => Object.values(bookings).filter((b) => b.studioId === studioId),
    [bookings, studioId],
  );

  // Apply the calendar filter bar (item 7). Deposit level is derived from the
  // booking's linked deposit so the deposit-state chips + hide-filled work.
  const visibleBookings = useMemo(
    () =>
      studioBookings.filter(
        (b) =>
          // Superseded records are history, not live slots: they never render as
          // an occupying tile (the vacated slot reads free). The reschedule
          // history marker reads them via its own lookup below.
          b.status !== 'superseded' &&
          // Cancelled / no-show only behind "Show cancelled" (or an explicit
          // status filter for them); they render as non-blocking ghosts.
          (isLiveBooking(b) || showCancelled || filters.statuses.includes(b.status)) &&
          bookingMatchesFilters(b, depositPaidLevel(deposits[b.depositId ?? '']), filters),
      ),
    [studioBookings, deposits, filters, showCancelled],
  );

  // Kept-original records, surfaced only as faint history ghosts on the calendar
  // (never as live tiles). Reschedules preserve the original visibly (15 Jul).
  const supersededBookings = useMemo(
    () => studioBookings.filter((b) => b.status === 'superseded'),
    [studioBookings],
  );

  const bookingsForArtistOnDay = (artistId: string, day: Date) =>
    visibleBookings.filter(
      (b) => b.artistId === artistId && isSameDay(new Date(b.start), day),
    );

  if (!studio) {
    return <p className="px-10 py-12 text-cream-soft italic">Unknown studio.</p>;
  }

  const dayRev = (date: Date, artistId?: string) => {
    let sum = 0;
    for (const b of studioBookings) {
      if (!isSameDay(new Date(b.start), date)) continue;
      if (artistId && b.artistId !== artistId) continue;
      sum +=
        b.sessionLength === 'multi-session'
          ? 2400
          : b.sessionLength === 'full-day'
            ? 2400
            : b.sessionLength === 'half-day'
              ? 1200
              : 400;
    }
    return sum;
  };

  return (
    <section className="px-6 py-6" data-testid={`studio-calendar-${studio.id}`}>
      {isError && !calendarData ? (
        <div className="px-8 py-4" data-testid="calendar-reconnecting">
          <ReconnectingNotice />
        </div>
      ) : null}
      <header className="flex items-end justify-between mb-4">
        <div>
          <p className="label-small-caps text-gold-bright">{studio.brand}</p>
          <h1 className="heading-display text-2xl text-cream mt-2">{studio.name} calendar</h1>
          <p className="font-inter text-[10px] text-cream-soft mt-1 uppercase tracking-widest">
            {studioArtists.length} artists rostered · {studioBookings.length} bookings this fortnight
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex border border-gold-dim/40 rounded-full overflow-hidden">
            <button
              type="button"
              className={clsx(
                'px-3 py-1.5 text-xs uppercase tracking-widest',
                view === 'day'
                  ? 'bg-gold/15 text-gold-bright'
                  : 'text-cream-soft hover:text-cream',
              )}
              onClick={() => setView('day')}
              data-testid="calendar-view-day"
            >
              Day
            </button>
            <button
              type="button"
              className={clsx(
                'px-3 py-1.5 text-xs uppercase tracking-widest',
                view === 'week'
                  ? 'bg-gold/15 text-gold-bright'
                  : 'text-cream-soft hover:text-cream',
              )}
              onClick={() => setView('week')}
              data-testid="calendar-view-week"
            >
              Week
            </button>
          </div>
          <button
            type="button"
            className="btn-quiet"
            onClick={() =>
              view === 'day' ? goToDay(addDays(pickedDay, -1)) : goToWeek(addDays(weekStart, -7))
            }
          >
            <ChevronLeft size={14} /> {view === 'day' ? 'Prev day' : 'Prev week'}
          </button>
          <MiniCalendarPopover
            value={view === 'day' ? pickedDay : weekStart}
            mode={view}
            todayIso={clockIso}
            label={
              view === 'day'
                ? format(pickedDay, 'EEE d MMM yyyy')
                : `Week of ${format(weekStart, 'd MMM yyyy')}`
            }
            onSelect={(day) => {
              if (view === 'day') goToDay(day);
              else goToWeek(day);
            }}
            testId="calendar-date-picker"
          />
          <button
            type="button"
            className="btn-quiet"
            onClick={() =>
              view === 'day' ? goToDay(addDays(pickedDay, 1)) : goToWeek(addDays(weekStart, 7))
            }
          >
            {view === 'day' ? 'Next day' : 'Next week'} <ChevronRight size={14} />
          </button>
          <button
            type="button"
            className="btn-ghost text-xs"
            onClick={() => goToDay(new Date(clockIso))}
          >
            <Calendar size={12} /> Today
          </button>
        </div>
      </header>

      {rebookForBookingId && rebookCustomer ? (
        <div
          data-testid="rebook-pick-banner"
          className="rounded-lg border border-gold-bright/50 bg-gold/10 px-4 py-3 mb-4 flex items-center justify-between"
        >
          <p className="font-serif text-cream">
            <span className="label-small-caps text-gold-bright mr-2">Picking rebook slot</span>
            for <span className="font-inter">{customerName(rebookCustomer)}</span> — click an open
            slot with{' '}
            {artists[focusState?.rebookArtistId ?? '']?.firstName ?? 'the artist'}. Sittings under 2
            weeks ask you to confirm the healing window.
          </p>
          <button
            type="button"
            onClick={() => navigate(`/floor/${rebookForBookingId}`)}
            className="btn-quiet text-[11px]"
            data-testid="rebook-pick-cancel"
          >
            Cancel
          </button>
        </div>
      ) : placeDeposit && rawCustomers[placeDeposit.customerId] ? (
        <div
          data-testid="place-booking-banner"
          className="rounded-lg border border-mint/60 bg-mint/10 px-4 py-3 mb-4 flex items-center justify-between"
        >
          <p className="font-serif text-cream flex items-center gap-2">
            <Sparkles size={14} className="text-mint" />
            <span className="label-small-caps text-mint mr-2">Deposit paid</span>
            Place{' '}
            <span className="font-inter">{customerName(rawCustomers[placeDeposit.customerId]!)}</span>
            &apos;s booking (${placeDeposit.amountAud} held). Click any open slot to confirm.
          </p>
          <button
            type="button"
            onClick={() => navigate(`/customer/${placeDeposit.customerId}`)}
            className="btn-quiet text-[11px]"
            data-testid="place-booking-cancel"
          >
            Back to record
          </button>
        </div>
      ) : bookForCustomerId && rawCustomers[bookForCustomerId] ? (
        <div
          data-testid="book-for-banner"
          className="rounded-lg border border-gold-bright/50 bg-gold/10 px-4 py-3 mb-4 flex items-center justify-between"
        >
          <p className="font-serif text-cream">
            <span className="label-small-caps text-gold-bright mr-2">Picking slot</span>
            for <span className="font-inter">{customerName(rawCustomers[bookForCustomerId]!)}</span>.
            Click any open slot below to confirm the booking.
          </p>
          <button
            type="button"
            onClick={() => navigate(`/customer/${bookForCustomerId}`)}
            className="btn-quiet text-[11px]"
            data-testid="book-for-cancel"
          >
            Cancel
          </button>
        </div>
      ) : null}

      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <CalendarFilterBar
            artists={studioArtists}
            filters={filters}
            onChange={setFilters}
            showArtistFilter={view === 'day'}
          />
          <button
            type="button"
            onClick={() => setShowCancelled((v) => !v)}
            aria-pressed={showCancelled}
            data-testid="calendar-show-cancelled"
            className={clsx(
              'px-3 py-1 rounded-full text-xs transition border mb-4',
              showCancelled
                ? 'bg-gold/15 text-gold-bright border-gold-bright/60'
                : 'text-cream-soft border-gold-dim/40 hover:text-cream hover:border-gold-bright/40',
            )}
          >
            Show cancelled
          </button>
        </div>
        {view === 'week' && activeWeekArtist ? (
          <div className="flex items-center gap-2 flex-wrap">
            <DoubleDayBadge
              count={weekArtistDoubleDays}
              name={activeWeekArtist.displayName}
              prominent
            />
            <span
              data-testid="sessions-budget-chip"
              title="Full-day session units this week (a double day counts as 2). Warning only - comp days stay manual."
              className="inline-flex items-baseline gap-1.5 rounded-full border border-gold-dim/50 bg-ink-black px-3 py-1"
            >
              <span
                className={`font-mono text-sm tabular-nums ${
                  weekSessionUnits > WEEKLY_SESSION_BUDGET ? 'text-blood' : 'text-cream'
                }`}
              >
                {weekSessionUnits} / {WEEKLY_SESSION_BUDGET}
              </span>
              <span className="label-small-caps text-cream-faint">sessions this week</span>
            </span>
          </div>
        ) : null}
      </div>

      {view === 'week' && studioArtists.length > 0 ? (
        <div
          className="flex items-center gap-1.5 flex-wrap mb-4"
          data-testid="week-artist-switcher"
        >
          <span className="font-inter text-[10px] text-cream-soft uppercase tracking-widest mr-1">
            Viewing
          </span>
          {studioArtists.map((a) => (
            <button
              key={a.id}
              type="button"
              onClick={() => setWeekArtistId(a.id)}
              data-testid={`week-artist-${a.id}`}
              aria-pressed={activeWeekArtist?.id === a.id}
              className={clsx(
                'px-3 py-1 rounded-full text-xs transition border',
                activeWeekArtist?.id === a.id
                  ? 'bg-gold/15 text-gold-bright border-gold-bright/60'
                  : 'text-cream-soft border-gold-dim/40 hover:text-cream hover:border-gold-bright/40',
              )}
            >
              {a.displayName}
            </button>
          ))}
        </div>
      ) : null}

      {movingId && bookings[movingId] ? (
        <div
          className="mb-3 flex items-center justify-between gap-3 rounded-md border border-gold-bright/50 bg-gold/10 px-3 py-2"
          data-testid="calendar-move-hint"
        >
          <span className="text-sm text-gold-bright">
            Moving{' '}
            {(() => {
              const cust = customers[bookings[movingId]!.customerId];
              return cust ? customerName(cust) : 'this booking';
            })()}
            {' '}— drag the highlighted booking to a new slot.
          </span>
          <button
            type="button"
            onClick={() => setMovingId(null)}
            className="font-inter text-[10px] uppercase tracking-widest text-cream-soft hover:text-cream"
          >
            Cancel
          </button>
        </div>
      ) : null}

      {view === 'day' ? (
        <DayView
          onDayOff={(artistId, day) => setDayOff({artistId, date: format(day, 'yyyy-MM-dd')})}
          day={pickedDay}
          artists={studioArtists}
          studioId={studio.id}
          rows={rows}
          bookings={visibleBookings}
          supersededBookings={supersededBookings}
          deposits={deposits}
          waivers={waivers}
          customers={customers}
          clockIso={clockIso}
          awaitingApprovalIds={awaitingApprovalBookingIds}
          moveBookingId={movingId}
          onMoveBooking={(bookingId, artistId, startIso) => {
            // Keep-original reschedule: a drag never commits on its own. Capture
            // the intended move and require a reason before it lands.
            setPendingMove({ bookingId, artistId, startIso });
          }}
          onSlot={(artistId, startIso) => {
            if (rebookForBookingId) {
              // Under 2 weeks: hold for a confirm. Otherwise return the slot now.
              if (
                rebookMinDateIso &&
                differenceInCalendarDays(new Date(startIso), new Date(rebookMinDateIso)) < 0
              ) {
                setPendingRebookSlot({ artistId, startIso });
              } else {
                confirmRebookSlot(artistId, startIso);
              }
              return;
            }
            if (placeDeposit) {
              // Reflect the placed booking into the calendar's query cache once
              // it lands, so the agent sees it with no round-trip until the
              // outbox echo / refetch reconciles. (The booking id is fresh, never
              // the deposit's old, dead hold — B4 8tqt.)
              void placeBookingFromDeposit({
                depositId: placeDeposit.id,
                artistId,
                startIso,
                via: 'agent',
              })?.then((r) => {
                const createdBooking = r.ok && r.bookingId ? useBrainStore.getState().bookings[r.bookingId] : undefined;
                if (!createdBooking) return;
                queryClient.setQueryData(
                  ['calendar', studioId, fromIso, toIso],
                  (prev: CalendarViewBundle | undefined) =>
                    prev
                      ? { ...prev, bookings: { ...prev.bookings, [createdBooking.id]: createdBooking } }
                      : prev,
                );
              });
              const cust = rawCustomers[placeDeposit.customerId];
              pushNotification({
                intensity: 'high',
                title: 'Booking placed',
                body: `${cust ? customerName(cust) : placeDeposit.customerId} confirmed with ${artists[artistId]?.firstName ?? artistId}.`,
                ttlMs: 5000,
              });
              navigate(`/customer/${placeDeposit.customerId}`, { replace: true });
              return;
            }
            setSlotChoice({ artistId, startIso });
          }}
          onBookingClick={(bookingId) => setDetailBookingId(bookingId)}
        />
      ) : activeWeekArtist ? (
        <WeekView
          onDayOff={(artistId, day) => setDayOff({artistId, date: format(day, 'yyyy-MM-dd')})}
          days={days}
          dayRev={dayRev}
          artist={activeWeekArtist}
          rows={rows}
          bookingsForArtistOnDay={bookingsForArtistOnDay}
          deposits={deposits}
          waivers={waivers}
          customers={customers}
          clockIso={clockIso}
          awaitingApprovalIds={awaitingApprovalBookingIds}
          onBookingClick={(bookingId) => setDetailBookingId(bookingId)}
          onAddOnDay={(date) => {
            const start = new Date(date);
            start.setHours(11, 0, 0, 0);
            // Today's add starts no earlier than now (tv-2na.10.10).
            setSlotChoice({
              artistId: activeWeekArtist.id,
              startIso: new Date(startNoEarlierThanNow(start.toISOString(), clockIso)).toISOString(),
            });
          }}
        />
      ) : (
        <p className="px-2 py-12 text-cream-soft italic">No artists rostered.</p>
      )}

      <footer className="mt-6 panel p-4 flex flex-wrap gap-x-6 gap-y-2 items-center max-w-5xl" data-testid="calendar-legend">
        <p className="label-small-caps">Deposit</p>
        <span className="inline-flex items-center gap-2 text-xs">
          <span className="h-3 w-3 rounded bg-gold-bright/90" /> Paid in full
        </span>
        <span className="inline-flex items-center gap-2 text-xs">
          <span className="h-3 w-3 rounded bg-gold-bright/40" /> Part-paid
        </span>
        <span className="inline-flex items-center gap-2 text-xs">
          <span className="h-3 w-3 rounded bg-gold-bright/10 border border-gold-bright/30" /> Awaiting
        </span>
        <span className="label-small-caps ml-2">Status</span>
        <span className="inline-flex items-center gap-2 text-xs">
          <span className="h-3 w-3 rounded-full bg-gold-bright" /> Booked
        </span>
        <span className="inline-flex items-center gap-2 text-xs">
          <span className="h-3 w-3 rounded-full bg-azure" /> Checked in
        </span>
        <span className="inline-flex items-center gap-2 text-xs">
          <span className="h-3 w-3 rounded-full bg-violet" /> In chair
        </span>
        <span className="inline-flex items-center gap-2 text-xs">
          <span className="h-3 w-3 rounded-full bg-mint" /> Done
        </span>
        <span className="font-mono text-xs text-cream-soft ml-auto">
          Sim clock: {simTimeFull(clockIso)}
        </span>
      </footer>

      {pendingRebookSlot ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-ink-black/60 px-4"
          data-testid="rebook-under-window"
        >
          <div className="panel p-6 max-w-md space-y-4">
            <p className="label-small-caps text-blood">Under the 2-week healing window</p>
            <p className="font-serif text-cream-soft">
              {format(new Date(pendingRebookSlot.startIso), 'EEE d MMM')} is sooner than 2 weeks after
              the session. Tattoos need time to heal — book it anyway, or pick a later day.
            </p>
            <div className="flex justify-between">
              <button
                type="button"
                className="btn-quiet text-sm"
                onClick={() => setPendingRebookSlot(null)}
                data-testid="rebook-under-window-cancel"
              >
                Pick a later day
              </button>
              <button
                type="button"
                className="btn-primary text-sm"
                onClick={() => {
                  confirmRebookSlot(pendingRebookSlot.artistId, pendingRebookSlot.startIso);
                  setPendingRebookSlot(null);
                }}
                data-testid="rebook-under-window-confirm"
              >
                Book anyway
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {newBooking ? (
        <NewBookingModal
          open
          studioId={studio.id}
          artistId={newBooking.artistId}
          startIso={newBooking.startIso}
          onClose={() => setNewBooking(null)}
          defaultCustomerId={bookForCustomerId}
        />
      ) : null}

      {slotChoice ? (
        <SlotActionChooser
          artistName={studioArtists.find((a) => a.id === slotChoice.artistId)?.displayName ?? 'Artist'}
          startIso={slotChoice.startIso}
          onNewAppointment={() => {
            setNewBooking({ artistId: slotChoice.artistId, startIso: slotChoice.startIso });
            setSlotChoice(null);
          }}
          onDayOff={() => {
            setDayOff({
              artistId: slotChoice.artistId,
              date: format(new Date(slotChoice.startIso), 'yyyy-MM-dd'),
            });
            setSlotChoice(null);
          }}
          onClose={() => setSlotChoice(null)}
        />
      ) : null}

      {dayOff ? (
        <DayOffModal artistId={dayOff.artistId} date={dayOff.date} onClose={() => setDayOff(null)} />
      ) : null}

      {detailBookingId && bookings[detailBookingId] ? (
        <BookingDetailCard
          booking={bookings[detailBookingId]!}
          customer={customers[bookings[detailBookingId]!.customerId]}
          artist={artists[bookings[detailBookingId]!.artistId]}
          deposit={deposits[bookings[detailBookingId]!.depositId ?? '']}
          onClose={() => setDetailBookingId(null)}
          onNavigateBooking={(id) => setDetailBookingId(id)}
          onViewCustomer={() => {
            const cId = bookings[detailBookingId]!.customerId;
            setDetailBookingId(null);
            navigate(`/customer/${cId}`, {
              state: { fromCalendarStudioId: studio.id, fromCalendarDayIso: pickedDay.toISOString() },
            });
          }}
        />
      ) : null}

      {pendingMove ? (
        <RescheduleReasonChooser
          customerName={
            customers[bookings[pendingMove.bookingId]?.customerId ?? '']
              ? customerName(customers[bookings[pendingMove.bookingId]!.customerId]!)
              : undefined
          }
          fromIso={bookings[pendingMove.bookingId]?.start ?? pendingMove.startIso}
          toIso={pendingMove.startIso}
          onPick={(reason, note) => {
            const cust = customers[bookings[pendingMove.bookingId]?.customerId ?? ''];
            const result = rescheduleBooking({
              bookingId: pendingMove.bookingId,
              reason,
              note,
              pathway: { mode: 'move', artistId: pendingMove.artistId, newStartIso: pendingMove.startIso },
              by: currentActorId() ?? 'system',
              at: clockIso,
            });
            // A refused move (the slot is firm-taken: rescheduleFlow has already
            // said "That slot was just taken — pick another time") keeps the
            // move mode on so staff can drop it on another slot.
            if (!result.ok) {
              setPendingMove(null);
              if (result.error !== 'slot-taken') setMovingId(null);
              return;
            }
            // Reflect both the superseded original and the new booking into
            // the calendar's query cache so the move shows immediately; the
            // outbox echo/refetch reconciles with the server afterward.
            {
              const freshBookings = useBrainStore.getState().bookings;
              const changed: Record<string, ReturnType<typeof useBrainStore.getState>['bookings'][string]> = {};
              if (result.supersededBookingId && freshBookings[result.supersededBookingId]) {
                changed[result.supersededBookingId] = freshBookings[result.supersededBookingId]!;
              }
              if (result.newBookingId && freshBookings[result.newBookingId]) {
                changed[result.newBookingId] = freshBookings[result.newBookingId]!;
              }
              if (Object.keys(changed).length) {
                queryClient.setQueryData(
                  ['calendar', studioId, fromIso, toIso],
                  (prev: CalendarViewBundle | undefined) =>
                    prev ? { ...prev, bookings: { ...prev.bookings, ...changed } } : prev,
                );
              }
            }
            pushNotification({
              intensity: 'info',
              title: 'Booking rescheduled',
              body: `${cust ? customerName(cust) : 'Booking'} rescheduled to ${simTimeFull(
                pendingMove.startIso,
              )}. The original slot is kept in history.`,
              ttlMs: 3500,
            });
            setPendingMove(null);
            setMovingId(null);
          }}
          onClose={() => {
            setPendingMove(null);
            setMovingId(null);
          }}
        />
      ) : null}
    </section>
  );
}

/**
 * Guarantees a QueryClient is available for `useCalendarView` even when this
 * screen mounts standalone (e.g. existing unit tests render it directly,
 * without the app root's `<QueryClientProvider>` from main.tsx). Nesting the
 * same shared `queryClient` singleton here is a no-op when an ancestor
 * provider already supplies it — same client instance, so no duplicate cache.
 */
export default function StudioCalendar() {
  // `/calendar/all` is the all-studios calendar (75n8): read-only, banded by studio.
  const { studioId } = useParams<{ studioId: string }>();
  return (
    <QueryClientProvider client={queryClient}>
      {studioId === ALL_STUDIOS_SCOPE ? <NetworkCalendar /> : <StudioCalendarInner />}
    </QueryClientProvider>
  );
}

type CalBooking = ReturnType<typeof useBrainStore.getState>['bookings'][string];

/** One laid-out item in a day column: a normal booking, or a stacked-holds group. */
type DayCell =
  | { kind: 'booking'; startMs: number; endMs: number; booking: CalBooking }
  | { kind: 'stack'; startMs: number; endMs: number; cluster: HoldCluster };

interface DayViewProps {
  day: Date;
  artists: Array<ReturnType<typeof useBrainStore.getState>['artists'][string]>;
  studioId: string;
  rows: string[];
  bookings: Array<ReturnType<typeof useBrainStore.getState>['bookings'][string]>;
  /** Superseded records for this studio — rendered as faint history ghosts. */
  supersededBookings: Array<ReturnType<typeof useBrainStore.getState>['bookings'][string]>;
  deposits: ReturnType<typeof useBrainStore.getState>['deposits'];
  waivers: ReturnType<typeof useBrainStore.getState>['waivers'];
  customers: ReturnType<typeof useBrainStore.getState>['customers'];
  clockIso: string;
  onSlot: (artistId: string, startIso: string) => void;
  onBookingClick: (bookingId: string) => void;
  onDayOff: (artistId: string, day: Date) => void;
  onMoveBooking: (bookingId: string, artistId: string, startIso: string) => void;
  moveBookingId?: string | null;
  awaitingApprovalIds: Set<string>;
}

function DayView({ onDayOff, day, artists, rows, bookings, supersededBookings, deposits, waivers, customers, clockIso, onSlot, onBookingClick, onMoveBooking, moveBookingId, awaitingApprovalIds }: DayViewProps) {
  return (
    <div className="flex">
      <div className="w-14 shrink-0 pt-12">
        {rows.map((r) => (
          <div key={r} className="h-[30px] font-mono text-[10px] text-cream-soft text-right pr-2">
            {r.endsWith(':00') ? r : ''}
          </div>
        ))}
      </div>
      <div className="flex-1 overflow-x-auto">
        <div
          className="grid"
          style={{
            gridTemplateColumns: `repeat(${artists.length}, minmax(140px, 1fr))`,
            gap: '4px',
          }}
        >
          {artists.map((artist) => {
            const allMine = bookings.filter(
              (b) => b.artistId === artist.id && isSameDay(new Date(b.start), day),
            );
            // Only live bookings occupy the column (lanes, slot conflicts, the
            // count). Cancelled / no-show render as click-through ghosts (G8).
            const myBookings = allMine.filter((b) => isLiveBooking(b));
            const myDead = allMine.filter((b) => !isLiveBooking(b));
            const mySuperseded = supersededBookings.filter(
              (b) => b.artistId === artist.id && isSameDay(new Date(b.start), day),
            );
            const off = resolveDayOff(artist.availability, day);
            const onShift = off === null;
            // Competing this-week ghost holds (overbookable, overlapping) collapse
            // into ONE stacked cell rather than splitting the column into skinny
            // lanes. Everything else - including a lone hold - lays out as today.
            // Only 2+ overlapping holds cluster; a lone hold stays in `myBookings`
            // and falls through the filter below to render as a normal tile.
            const { clusters } = groupOverbookableHolds(myBookings);
            const clusteredIds = new Set(
              clusters.flatMap((c) => c.holds.map((h) => h.id)),
            );
            const cells: DayCell[] = [
              ...myBookings
                .filter((b) => !clusteredIds.has(b.id))
                .map((booking) => {
                  const startMs = new Date(booking.start).getTime();
                  return {
                    kind: 'booking' as const,
                    startMs,
                    endMs: startMs + booking.durationMin * 60_000,
                    booking,
                  };
                }),
              ...clusters.map((cluster) => ({
                kind: 'stack' as const,
                startMs: cluster.startMs,
                endMs: cluster.endMs,
                cluster,
              })),
            ];
            // Lane-pack the cells so concurrent sittings sit side-by-side; a
            // stacked-holds cell packs as a single item at normal booking width.
            const laneEnds: number[] = [];
            const laidDay = cells
              .slice()
              .sort((a, b) => a.startMs - b.startMs)
              .map((cell) => {
                let lane = laneEnds.findIndex((endAt) => endAt <= cell.startMs);
                if (lane === -1) {
                  laneEnds.push(cell.endMs);
                  lane = laneEnds.length - 1;
                } else {
                  laneEnds[lane] = cell.endMs;
                }
                return { cell, lane };
              });
            const totalLanesDay = Math.max(1, laneEnds.length);
            return (
              <div
                key={artist.id}
                className={clsx(
                  'bg-ink-deep rounded-md border border-gold-dim/15',
                  !onShift ? 'opacity-40' : '',
                )}
                data-testid={`day-col-${artist.id}`}
              >
                <div className="text-center py-2 border-b border-gold-dim/15">
                  <p className="font-inter text-sm text-cream">{artist.displayName}</p>
                  {onShift ? (
                    <p className="font-inter text-[10px] text-cream-soft uppercase tracking-widest mt-0.5">
                      {myBookings.length} {myBookings.length === 1 ? 'booking' : 'bookings'}
                    </p>
                  ) : (
                    <p className="font-inter text-[10px] text-blood uppercase tracking-widest mt-0.5">
                      {off ? dayOffTileTone(off.kind).label : 'RDO'}
                    </p>
                  )}
                </div>
                <div
                  className="relative overflow-hidden"
                  style={{ height: `${rows.length * SLOT_HEIGHT_PX}px` }}
                >
                  {rows.map((row, i) => {
                    const [hh, mm] = row.split(':').map((n) => parseInt(n, 10));
                    const slotDate = new Date(day);
                    slotDate.setHours(hh ?? 10, mm ?? 0, 0, 0);
                    const conflict = myBookings.some((b) => {
                      const bs = new Date(b.start).getTime();
                      const be = bs + b.durationMin * 60 * 1000;
                      const ts = slotDate.getTime();
                      return ts >= bs && ts < be;
                    });
                    // A slot already started on the sim clock can't be booked
                    // (tv-2na.10.10): today is clipped to now, a past day has none.
                    const past = slotDate.getTime() < Date.parse(clockIso);
                    const blocked = conflict || !onShift || past;
                    return (
                      <button
                        key={i}
                        type="button"
                        onClick={() => onSlot(artist.id, slotDate.toISOString())}
                        disabled={blocked}
                        onDragOver={(e) => {
                          if (!blocked) {
                            e.preventDefault();
                            e.dataTransfer.dropEffect = 'move';
                            e.currentTarget.classList.add('bg-mint/15');
                          }
                        }}
                        onDragLeave={(e) => e.currentTarget.classList.remove('bg-mint/15')}
                        onDrop={(e) => {
                          e.currentTarget.classList.remove('bg-mint/15');
                          const id = e.dataTransfer.getData('text/booking-id');
                          if (id) onMoveBooking(id, artist.id, slotDate.toISOString());
                        }}
                        className={clsx(
                          'absolute left-0 right-0 border-t border-transparent group',
                          blocked
                            ? 'cursor-default'
                            : 'hover:bg-gold/5 cursor-pointer',
                          i % 2 === 0 ? 'border-t-gold-dim/20' : 'border-t-gold-dim/8',
                        )}
                        style={{
                          top: i * SLOT_HEIGHT_PX,
                          height: SLOT_HEIGHT_PX,
                        }}
                        data-testid={`slot-${artist.id}-${row}`}
                      >
                        {!blocked ? (
                          <Plus
                            size={10}
                            className="opacity-0 group-hover:opacity-60 text-gold-bright mx-auto"
                          />
                        ) : null}
                      </button>
                    );
                  })}
                  {mySuperseded.map((b) => {
                    // Ghost marker on the vacated slot: the original is kept for
                    // history (15 Jul). Rendered behind live tiles so a re-booked
                    // slot's real booking wins; click opens its full history.
                    const gStart = new Date(b.start);
                    const gMinutes = (gStart.getHours() - FIRST_HOUR) * 60 + gStart.getMinutes();
                    const gVis = Math.max(0, gMinutes);
                    if (gVis >= rows.length * SLOT_MIN) return null;
                    const last = b.rescheduleHistory?.[b.rescheduleHistory.length - 1];
                    const cust = customers[b.customerId];
                    const movedTo =
                      last && last.toIso !== last.fromIso ? simTimeFull(last.toIso) : 'no new date yet';
                    const ghostTip = `${cust ? customerName(cust) : 'Booking'} was here → moved to ${movedTo}${
                      last?.reason ? ` · ${RESCHEDULE_REASON_LABEL[last.reason]}` : ''
                    } · by ${actorLabel(last?.by ?? 'system')}${last?.note ? ` · "${last.note}"` : ''}`;
                    return (
                      <button
                        key={`sup-${b.id}`}
                        type="button"
                        title={ghostTip}
                        onClick={() => onBookingClick(b.id)}
                        className="absolute left-0 right-0 mx-0.5 flex items-center gap-1 rounded-md border border-dashed border-cream-faint/40 bg-cream-faint/5 px-2 text-cream-faint hover:border-gold-bright/50"
                        style={{ top: (gVis / SLOT_MIN) * SLOT_HEIGHT_PX, height: SLOT_HEIGHT_PX - 2 }}
                        data-testid={`superseded-${b.id}`}
                      >
                        <History size={10} />
                        <span className="text-[10px] uppercase tracking-widest line-through truncate">
                          {cust ? customerName(cust) : 'Moved'} · moved
                        </span>
                      </button>
                    );
                  })}
                  {myDead.map((b) => (
                    <DeadGhostTile key={`dead-${b.id}`} booking={b} customers={customers} rows={rows} />
                  ))}
                  {laidDay.map(({ cell, lane }) => {
                    const laneWidthDay = 100 / totalLanesDay;
                    // Geometry is shared by both cell kinds: minutes from the 9am
                    // grid top, then the same left/width a single booking occupies.
                    const cellStart = new Date(cell.startMs);
                    const startMinutes =
                      (cellStart.getHours() - FIRST_HOUR) * 60 + cellStart.getMinutes();
                    const durationMin = (cell.endMs - cell.startMs) / 60_000;
                    // Clip to the visible 9-21 grid. A session that starts before
                    // opening is trimmed to the top edge (not clamped at full
                    // height, which would overshoot and stack on other early
                    // tiles); one that falls entirely outside the window is skipped.
                    const visStart = Math.max(0, startMinutes);
                    const visEnd = Math.min(rows.length * SLOT_MIN, startMinutes + durationMin);
                    if (visEnd <= visStart) return null;
                    const top = (visStart / SLOT_MIN) * SLOT_HEIGHT_PX;
                    const height = ((visEnd - visStart) / SLOT_MIN) * SLOT_HEIGHT_PX;
                    const left = `calc(${lane * laneWidthDay}% + 2px)`;
                    const width = `calc(${laneWidthDay}% - 4px)`;
                    if (cell.kind === 'stack') {
                      return (
                        <StackedHoldsTile
                          key={`stack-${cell.cluster.id}`}
                          id={cell.cluster.id}
                          holds={cell.cluster.holds}
                          deposits={deposits}
                          customers={customers}
                          clockIso={clockIso}
                          onOpen={onBookingClick}
                          style={{ top, height, left, width }}
                        />
                      );
                    }
                    const b = cell.booking;
                    const start = new Date(b.start);
                    const cust = customers[b.customerId];
                    const tone = bookingTileTone(b, tileDeposit(b, deposits));
                    // Lifecycle label + confirmation badge + deposit chip + ghost,
                    // from the W4 contract so the tile matches the Floor (G9).
                    const tile = bookingTileState(b, tone.shade.level, clockIso);
                    // Always-on waiver indicator: reflects all four lifecycle
                    // states (not just signed) — a chip on tall tiles, a dot on
                    // short ones. Derived from the subscribed `waivers` slice so
                    // a sent/signed waiver re-renders the tile.
                    const waiverStatus = pickLatestWaiver(waivers, b.id)?.status ?? 'not-sent';
                    const wTone = waiverTone(waiverStatus);
                    const price = bookingPriceAud(b, artist);
                    const lastResched = b.rescheduleHistory?.[b.rescheduleHistory.length - 1];
                    const tooltip = [
                      [tile.lifecycle, tile.badge?.label, tile.depositChip].filter(Boolean).join(' · '),
                      lastResched
                        ? `Rescheduled ${b.rescheduleHistory!.length}× · moved from ${simTimeFull(
                            lastResched.fromIso,
                          )} · ${RESCHEDULE_REASON_LABEL[lastResched.reason ?? 'other']} · by ${actorLabel(lastResched.by)}`
                        : '',
                    ]
                      .filter(Boolean)
                      .join('\n');
                    return (
                      <button
                        key={b.id}
                        type="button"
                        draggable
                        title={tooltip}
                        onDragStart={(e) => {
                          e.dataTransfer.effectAllowed = 'move';
                          e.dataTransfer.setData('text/booking-id', b.id);
                          e.currentTarget.classList.add('opacity-50');
                        }}
                        onDragEnd={(e) => e.currentTarget.classList.remove('opacity-50')}
                        onClick={() => onBookingClick(b.id)}
                        style={{ top, height, left, width }}
                        className={clsx(
                          'absolute rounded-md px-2.5 py-1.5 text-left transition overflow-hidden shadow-ink-soft cursor-grab active:cursor-grabbing hover:brightness-110',
                          tone.className,
                          tile.ghostClass,
                          awaitingApprovalIds.has(b.id) && 'ring-1 ring-orange/60',
                          moveBookingId === b.id && 'ring-2 ring-gold-bright animate-pulse z-10',
                        )}
                        data-testid={`booking-${b.id}`}
                        data-ghost={tile.ghost ?? undefined}
                      >
                        <span className="font-inter text-sm block leading-tight truncate">
                          {cust ? customerName(cust) : b.notes ?? 'Booking'}
                        </span>
                        <span className="font-inter text-[11px] uppercase tracking-widest mt-0.5 flex items-center gap-1">
                          {lastResched ? (
                            <History size={10} className="shrink-0" aria-label="Rescheduled" />
                          ) : null}
                          <span className="truncate">
                            {format(start, 'HH:mm')}
                            {b.durationMin >= 60
                              ? ` · ${(b.durationMin / 60).toFixed(b.durationMin % 60 ? 1 : 0)}h`
                              : ` · ${b.durationMin}m`}
                            {' · '}
                            {aud(price)}
                          </span>
                        </span>
                        {height >= 90 ? (
                          <span className="mt-1 flex flex-wrap gap-1" data-testid={`booking-chips-${b.id}`}>
                            <span
                              data-testid={`booking-lifecycle-${b.id}`}
                              className={clsx(
                                CALENDAR_PILL,
                                CALENDAR_PILL_TONE.cream,
                              )}
                            >
                              {tile.lifecycle}
                            </span>
                            {tile.badge ? (
                              <span
                                data-testid={`booking-confirmation-${b.id}`}
                                className={clsx(
                                  CALENDAR_PILL,
                                  CALENDAR_PILL_TONE[tile.badge.tone],
                                )}
                              >
                                {tile.badge.label}
                              </span>
                            ) : null}
                            {tile.depositChip ? (
                              <span className={clsx(CALENDAR_PILL, CALENDAR_PILL_TONE.gold)}>
                                {tile.depositChip}
                              </span>
                            ) : null}
                            <span
                              data-testid={`booking-waiver-${b.id}`}
                              data-variant="chip"
                              title={wTone.label}
                              className={clsx(
                                CALENDAR_PILL,
                                CALENDAR_PILL_TONE[waiverStatus === 'signed' ? 'mint' : waiverStatus === 'expired' ? 'blood' : waiverStatus === 'sent' ? 'gold' : 'cream'],
                              )}
                            >
                              {wTone.label}
                            </span>
                          </span>
                        ) : (
                          <span
                            data-testid={`booking-waiver-${b.id}`}
                            data-variant="dot"
                            title={wTone.label}
                            aria-label={wTone.label}
                            className={clsx('absolute bottom-1 right-1.5 h-2 w-2 rounded-full', wTone.dot)}
                          />
                        )}
                        {awaitingApprovalIds.has(b.id) ? (
                          <span
                            className="absolute top-1 right-1 rounded-sm bg-orange px-1 py-0.5 text-[8px] font-mono font-bold uppercase tracking-wide text-ink-black shadow-ink-soft"
                            data-testid={`booking-awaiting-${b.id}`}
                            title="Awaiting no-deposit approval"
                          >
                            Approval
                          </span>
                        ) : payAtStudioDepositFor(b, deposits) ? (
                          // B4 (8tqu): the client pays the deposit at the desk; held until then.
                          <span
                            className="absolute top-1 right-1 rounded-sm bg-azure px-1 py-0.5 text-[8px] font-mono font-bold uppercase tracking-wide text-ink-black shadow-ink-soft"
                            data-testid={`booking-pay-at-studio-${b.id}`}
                            title="Pays the deposit at the desk on arrival"
                          >
                            Pay at studio
                          </span>
                        ) : null}
                      </button>
                    );
                  })}
                  {off ? (
                    <button
                      type="button"
                      onClick={() => onDayOff(artist.id, day)}
                      aria-label={`Edit day off for ${artist.displayName} on ${format(day, 'd MMM')}`}
                      data-testid={`day-off-tile-${artist.id}`}
                      title={off.note ?? dayOffTileTone(off.kind).label}
                      className={clsx(
                        'absolute inset-x-1 top-0 flex flex-col items-center justify-center rounded-md text-[11px]',
                        dayOffTileTone(off.kind).border,
                        dayOffTileTone(off.kind).fill,
                        dayOffTileTone(off.kind).text,
                      )}
                      style={{ height: rows.length * SLOT_HEIGHT_PX }}
                    >
                      <span className="font-semibold uppercase tracking-wide">
                        {dayOffTileTone(off.kind).label}
                      </span>
                      {off.note ? (
                        <span className="mt-0.5 px-2 text-center opacity-80 line-clamp-2">{off.note}</span>
                      ) : null}
                    </button>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/**
 * A cancelled / no-show booking behind "Show cancelled" (W4 G8): a faint dashed
 * ghost at its old time that lets clicks through to the (free) slot beneath and
 * carries no deposit chip — the slot is bookable, the ghost is only history.
 */
function DeadGhostTile({
  booking: b,
  customers,
  rows,
  compact = false,
}: {
  booking: CalBooking;
  customers: ReturnType<typeof useBrainStore.getState>['customers'];
  rows: string[];
  compact?: boolean;
}) {
  const start = new Date(b.start);
  const startMinutes = (start.getHours() - FIRST_HOUR) * 60 + start.getMinutes();
  const visStart = Math.max(0, startMinutes);
  const visEnd = Math.min(rows.length * SLOT_MIN, startMinutes + b.durationMin);
  if (visEnd <= visStart) return null;
  const cust = customers[b.customerId];
  const tone = bookingStatusTone(b.status);
  return (
    <div
      aria-hidden="true"
      data-testid={`booking-${b.id}`}
      data-ghost="dead"
      title={`${cust ? customerName(cust) : 'Booking'} · ${tone.label} · slot is free`}
      className={clsx(
        'absolute left-0 right-0 mx-0.5 rounded-md border px-2 py-1 overflow-hidden',
        tone.border,
        tone.fill,
        GHOST_TILE_CLASS.dead,
      )}
      style={{
        top: (visStart / SLOT_MIN) * SLOT_HEIGHT_PX,
        height: ((visEnd - visStart) / SLOT_MIN) * SLOT_HEIGHT_PX,
      }}
    >
      <span className={clsx('font-inter block truncate', compact ? 'text-[11px]' : 'text-sm', tone.text)}>
        {cust ? customerName(cust) : 'Booking'}
      </span>
      <span className="font-inter text-[9px] uppercase tracking-widest text-cream-faint">{tone.label}</span>
    </div>
  );
}

interface WeekViewProps {
  days: Date[];
  dayRev: (d: Date, artistId?: string) => number;
  artist: ArtistLike;
  rows: string[];
  bookingsForArtistOnDay: (
    artistId: string,
    day: Date,
  ) => Array<ReturnType<typeof useBrainStore.getState>['bookings'][string]>;
  deposits: ReturnType<typeof useBrainStore.getState>['deposits'];
  waivers: ReturnType<typeof useBrainStore.getState>['waivers'];
  customers: ReturnType<typeof useBrainStore.getState>['customers'];
  clockIso: string;
  onBookingClick: (bookingId: string) => void;
  onDayOff: (artistId: string, day: Date) => void;
  onAddOnDay: (day: Date) => void;
  awaitingApprovalIds: Set<string>;
}

/**
 * A single artist's week: 7 day-columns showing only `artist`'s bookings. One
 * artist can't be in two chairs at once, so tiles are full-width and readable;
 * the lane allocator only kicks in for the rare overlap (e.g. a collab). Days the
 * artist isn't working (RDO / leave / off-pattern) are shaded and badged.
 */
function WeekView({
  days,
  dayRev,
  artist,
  rows,
  bookingsForArtistOnDay,
  deposits,
  waivers,
  customers,
  clockIso,
  onBookingClick,
  onAddOnDay,
  onDayOff,
  awaitingApprovalIds,
}: WeekViewProps) {
  return (
    <div className="flex">
      <div className="w-14 shrink-0 pt-14">
        {rows.map((r) => (
          <div key={r} className="h-[30px] font-mono text-[10px] text-cream-soft text-right pr-2">
            {r.endsWith(':00') ? r : ''}
          </div>
        ))}
      </div>
      <div className="flex-1 grid grid-cols-7 gap-1">
        {days.map((day) => {
          const allDay = bookingsForArtistOnDay(artist.id, day);
          // Live bookings lay out; cancelled / no-show are click-through ghosts (G8).
          const deadDay = allDay.filter((b) => !isLiveBooking(b));
          const dayBookings = allDay
            .filter((b) => isLiveBooking(b))
            .sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());

          // Competing overbookable holds collapse into one stacked cell here too,
          // for parity with the day view; everything else lane-packs as before.
          const { clusters } = groupOverbookableHolds(dayBookings);
          const clusteredIds = new Set(clusters.flatMap((c) => c.holds.map((h) => h.id)));
          const cells: DayCell[] = [
            ...dayBookings
              .filter((b) => !clusteredIds.has(b.id))
              .map((booking) => {
                const startMs = new Date(booking.start).getTime();
                return {
                  kind: 'booking' as const,
                  startMs,
                  endMs: startMs + booking.durationMin * 60_000,
                  booking,
                };
              }),
            ...clusters.map((cluster) => ({
              kind: 'stack' as const,
              startMs: cluster.startMs,
              endMs: cluster.endMs,
              cluster,
            })),
          ];
          // Lane allocator: pack the rare overlapping cells into sub-columns.
          const laneEnds: number[] = [];
          const laid = cells
            .slice()
            .sort((a, b) => a.startMs - b.startMs)
            .map((cell) => {
              let lane = laneEnds.findIndex((endAt) => endAt <= cell.startMs);
              if (lane === -1) {
                laneEnds.push(cell.endMs);
                lane = laneEnds.length - 1;
              } else {
                laneEnds[lane] = cell.endMs;
              }
              return { cell, lane };
            });
          const totalLanes = Math.max(1, laneEnds.length);

          const off = dayOffFor(artist, day);
          const isOff = off !== null;
          // A day already over on the sim clock takes no new booking (tv-2na.10.10).
          const dayOver = addDays(startOfDay(day), 1).getTime() <= Date.parse(clockIso);

          return (
            <div
              key={day.toISOString()}
              className={clsx(
                'bg-ink-deep rounded-md border border-gold-dim/15',
                isOff && 'opacity-50',
              )}
            >
              <div className="flex items-center justify-between py-2 px-2 border-b border-gold-dim/15">
                <div>
                  <p className="font-inter text-sm text-cream">{format(day, 'EEE d')}</p>
                  {off ? (
                    <p className="font-inter text-[10px] text-blood uppercase tracking-widest mt-0.5">
                      {dayOffTileTone(off.kind).label}
                    </p>
                  ) : (
                    <p className="font-inter text-[10px] text-gold-dim uppercase mt-0.5">
                      {dayBookings.length} · {aud(dayRev(day, artist.id))}
                    </p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => onAddOnDay(day)}
                  disabled={isOff || dayOver}
                  className={clsx(
                    'text-gold-bright hover:text-gold transition',
                    (isOff || dayOver) && 'opacity-30 pointer-events-none',
                  )}
                  data-testid={`week-add-${format(day, 'yyyy-MM-dd')}`}
                  aria-label="Add booking"
                >
                  <Plus size={14} />
                </button>
              </div>
              <div className="grid grid-cols-1">
                <div className="relative overflow-hidden" style={{ height: `${rows.length * SLOT_HEIGHT_PX}px` }}>
                  {rows.map((_r, i) => (
                    <div
                      key={i}
                      className={clsx(
                        'border-t',
                        i === 0
                          ? 'border-transparent'
                          : i % 2 === 0
                            ? 'border-gold-dim/20'
                            : 'border-gold-dim/8',
                      )}
                      style={{ height: `${SLOT_HEIGHT_PX}px` }}
                    />
                  ))}
                  {deadDay.map((b) => (
                    <DeadGhostTile key={`dead-${b.id}`} booking={b} customers={customers} rows={rows} compact />
                  ))}
                  {laid.map(({ cell, lane }) => {
                    const laneWidth = 100 / totalLanes;
                    const cellStart = new Date(cell.startMs);
                    const startMinutes =
                      (cellStart.getHours() - FIRST_HOUR) * 60 + cellStart.getMinutes();
                    const durationMin = (cell.endMs - cell.startMs) / 60_000;
                    // Clip to the visible 9-21 grid (see DayView) so pre-open
                    // sessions trim to the top edge instead of stacking there.
                    const visStart = Math.max(0, startMinutes);
                    const visEnd = Math.min(rows.length * SLOT_MIN, startMinutes + durationMin);
                    if (visEnd <= visStart) return null;
                    const top = (visStart / SLOT_MIN) * SLOT_HEIGHT_PX;
                    const height = ((visEnd - visStart) / SLOT_MIN) * SLOT_HEIGHT_PX;
                    const left = `calc(${lane * laneWidth}% + 2px)`;
                    const width = `calc(${laneWidth}% - 4px)`;
                    if (cell.kind === 'stack') {
                      return (
                        <StackedHoldsTile
                          key={`stack-${cell.cluster.id}`}
                          id={cell.cluster.id}
                          holds={cell.cluster.holds}
                          deposits={deposits}
                          customers={customers}
                          clockIso={clockIso}
                          onOpen={onBookingClick}
                          style={{ top, height, left, width }}
                        />
                      );
                    }
                    const b = cell.booking;
                    const start = new Date(b.start);
                    const cust = customers[b.customerId];
                    const tone = bookingTileTone(b, tileDeposit(b, deposits));
                    const tile = bookingTileState(b, tone.shade.level, clockIso);
                    // Always-on waiver dot, derived from the subscribed `waivers`
                    // slice so a sent/signed waiver re-renders the tile.
                    const wTone = waiverTone(pickLatestWaiver(waivers, b.id)?.status ?? 'not-sent');
                    return (
                      <button
                        key={b.id}
                        type="button"
                        onClick={() => onBookingClick(b.id)}
                        style={{ top, height, left, width }}
                        title={[tile.lifecycle, tile.badge?.label, tile.depositChip].filter(Boolean).join(' · ')}
                        className={clsx(
                          'absolute rounded-sm px-1.5 py-1 text-left transition overflow-hidden hover:brightness-110',
                          tone.className,
                          tile.ghostClass,
                          awaitingApprovalIds.has(b.id) && 'ring-1 ring-orange/60',
                        )}
                        data-testid={`booking-${b.id}`}
                        data-ghost={tile.ghost ?? undefined}
                      >
                        <span className="font-inter text-[11px] block leading-tight truncate">
                          {cust ? customerName(cust) : 'Booking'}
                        </span>
                        <span className="font-inter text-[9px] uppercase tracking-widest block leading-tight truncate opacity-80">
                          {format(start, 'HH:mm')} · {sessionLabel(b.sessionLength)}
                        </span>
                        {/* Compact tiles carry the waiver as an always-on dot. */}
                        <span
                          data-testid={`booking-waiver-${b.id}`}
                          data-variant="dot"
                          title={wTone.label}
                          aria-label={wTone.label}
                          className={clsx('absolute bottom-1 right-1 h-1.5 w-1.5 rounded-full', wTone.dot)}
                        />
                        {payAtStudioDepositFor(b, deposits) ? (
                          <span
                            className="font-inter text-[8px] uppercase tracking-widest block leading-tight truncate text-azure"
                            data-testid={`booking-pay-at-studio-${b.id}`}
                          >
                            Pay at studio
                          </span>
                        ) : null}
                      </button>
                    );
                  })}
                  {off ? (
                    <button
                      type="button"
                      onClick={() => onDayOff(artist.id, day)}
                      aria-label={`Edit day off for ${artist.displayName} on ${format(day, 'd MMM')}`}
                      data-testid={`week-day-off-tile-${format(day, 'yyyy-MM-dd')}`}
                      title={off.note ?? dayOffTileTone(off.kind).label}
                      className={clsx(
                        'absolute inset-x-1 top-0 flex flex-col items-center justify-center rounded-md text-[11px]',
                        dayOffTileTone(off.kind).border,
                        dayOffTileTone(off.kind).fill,
                        dayOffTileTone(off.kind).text,
                      )}
                      style={{ height: rows.length * SLOT_HEIGHT_PX }}
                    >
                      <span className="font-semibold uppercase tracking-wide">
                        {dayOffTileTone(off.kind).label}
                      </span>
                      {off.note ? (
                        <span className="mt-0.5 px-2 text-center opacity-80 line-clamp-2">{off.note}</span>
                      ) : null}
                    </button>
                  ) : null}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
